import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { BoardItem } from '../../types/board.js';
import { getAvatarUrl } from '../../services/agent-api.js';
import { PRIORITY_LABELS, SOURCE_TYPE_LABELS, isAgentAssignee, getAssigneeLabel } from './board-constants.js';
import './cp-execution-status-badge.js';

const MODEL_OPTIONS = [
  { value: 'claude-sonnet-4-6', label: 'Sonnet' },
  { value: 'claude-opus-4-6', label: 'Opus' },
  { value: 'claude-haiku-4-5-20251001', label: 'Haiku' },
] as const;

@customElement('cp-board-card')
export class CpBoardCard extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) item!: BoardItem;
  @property({ type: Boolean }) readonly = false;
  @property({ type: Boolean }) expanded = false;
  @property({ type: Boolean }) showExecutionBadge = false;

  private _onClick(): void {
    this.dispatchEvent(new CustomEvent('board-item-select', {
      bubbles: true,
      composed: true,
      detail: { item: this.item },
    }));
  }

  private _onModelChange(e: Event): void {
    e.stopPropagation();
    const model = (e.target as HTMLSelectElement).value;
    this.dispatchEvent(new CustomEvent('board-model-change', {
      bubbles: true,
      composed: true,
      detail: { itemId: this.item.id, model },
    }));
  }

  private _onModelClick(e: Event): void {
    e.stopPropagation();
  }

  private _onClaudeClick(e: Event): void {
    e.stopPropagation();
    this.dispatchEvent(new CustomEvent('open-project-claude', {
      bubbles: true,
      composed: true,
      detail: {
        projectPath: this.item.sourceProjectPath,
        projectName: this.item.sourceProject,
      },
    }));
  }

  private _renderOwnerBadge() {
    const { ownerName } = this.item;
    if (!ownerName) return nothing;

    return html`
      <span class="cp-board-card__owner">${ownerName}</span>
    `;
  }

  private _renderAssignee() {
    const { assignee } = this.item;
    const isAgent = isAgentAssignee(assignee);

    if (isAgent) {
      const label = assignee!;
      return html`
        <span class="cp-board-card__assignee cp-board-card__assignee--agent">
          <span class="cp-board-card__robot-icon" aria-label="Agent-Task">&#x1F916;</span>
          ${label}
        </span>
      `;
    }

    const label = assignee ? getAssigneeLabel(assignee) : '';
    if (!label) return nothing;
    return html`
      <span class="cp-board-card__assignee">${label}</span>
    `;
  }

  private _renderAgentBadge() {
    const { agent } = this.item;
    if (!agent) return nothing;

    const initial = agent.name.charAt(0).toUpperCase();

    return html`
      <span class="cp-board-card__agent-badge" title="Agent: ${agent.name}">
        ${agent.has_avatar
          ? html`<img class="cp-board-card__agent-avatar" src="${getAvatarUrl(agent.id)}" alt="${agent.name}" />`
          : html`<span class="cp-board-card__agent-initial" style="background-color: ${agent.color}">${initial}</span>`
        }
        <span class="cp-board-card__agent-name">${agent.name}</span>
      </span>
    `;
  }

  private _renderSkillBadges() {
    const { skillNames } = this.item;
    if (!skillNames?.length) return nothing;

    return skillNames.map(name => html`
      <span class="cp-board-card__skill-badge" title="Skill: ${name}">
        <span class="cp-board-card__skill-icon">&#x2699;</span>
        <span class="cp-board-card__skill-name">${name}</span>
      </span>
    `);
  }

  private _renderExecutionIndicator() {
    const { executionStatus, queuePosition, executionError } = this.item;

    if (executionStatus === 'running') {
      return html`<span class="cp-board-card__execution-spinner" aria-label="Wird ausgeführt"></span>`;
    }

    if (executionStatus === 'queued') {
      return html`<span class="cp-board-card__queue-badge">Queue: #${queuePosition ?? '?'}</span>`;
    }

    if (executionStatus === 'failed') {
      const tooltip = executionError ?? 'Execution fehlgeschlagen';
      return html`<span class="cp-board-card__error-badge" title="${tooltip}">Fehler</span>`;
    }

    return nothing;
  }

  private _renderModelSelector() {
    const { item } = this;
    if (this.readonly) return nothing;
    if (item.sourceType !== 'backlog') return nothing;

    const currentModel = item.executionModel ?? 'claude-sonnet-4-6';

    return html`
      <div class="cp-board-card__model-row">
        <select
          class="cp-board-card__model-select"
          .value=${currentModel}
          @change=${this._onModelChange}
          @click=${this._onModelClick}
        >
          ${MODEL_OPTIONS.map(opt => html`
            <option value=${opt.value} ?selected=${currentModel === opt.value}>${opt.label}</option>
          `)}
        </select>
      </div>
    `;
  }

  private _renderProgress() {
    const { progress } = this.item;
    if (!progress) return nothing;

    const percent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

    return html`
      <div class="cp-board-card__progress">
        <div class="cp-board-card__progress-bar">
          <div class="cp-board-card__progress-fill" style="width: ${percent}%"></div>
        </div>
        <span class="cp-board-card__progress-text">${progress.done}/${progress.total}</span>
      </div>
    `;
  }

  render() {
    const { item } = this;
    if (!item) return nothing;

    const classes = ['cp-board-card'];
    if (this.readonly) classes.push('cp-board-card--readonly');

    return html`
      <div class="${classes.join(' ')}" @click=${this._onClick}>
        <div class="cp-board-card__header">
          ${item.sourceType === 'spec' ? html`
            <span class="cp-board-card__expand-icon ${this.expanded ? 'cp-board-card__expand-icon--expanded' : ''}">&#9654;</span>
          ` : nothing}
          <span class="cp-board-card__title">${item.title}</span>
          ${this.showExecutionBadge
            ? html`<cp-execution-status-badge .ticketId=${this.item.id}></cp-execution-status-badge>`
            : this._renderExecutionIndicator()}
          ${item.sourceProjectPath ? html`
            <button
              class="cp-board-card__claude-btn"
              @click=${this._onClaudeClick}
              title="Claude Code im Projekt oeffnen"
            >&gt;_</button>
          ` : nothing}
          <span class="cp-board-card__priority cp-board-card__priority--${item.priority}">
            ${PRIORITY_LABELS[item.priority] ?? item.priority}
          </span>
        </div>
        <div class="cp-board-card__meta">
          <span class="cp-board-card__project-badge">${item.sourceProject}</span>
          <span class="cp-board-card__source-type">${SOURCE_TYPE_LABELS[item.sourceType] ?? item.sourceType}</span>
          ${this._renderOwnerBadge()}
          ${this._renderAssignee()}
          ${this._renderAgentBadge()}
          ${this._renderSkillBadges()}
        </div>
        ${this._renderModelSelector()}
        ${this._renderProgress()}
        ${this.readonly ? html`<span class="cp-board-card__readonly-badge">Nur lesen</span>` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-board-card': CpBoardCard;
  }
}
